import { motion } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Leaf,
  TreePine,
  Sprout,
  Droplets,
  Sun,
  Flower,
  ArrowRight,
  CheckCircle,
  Clock,
  Shield,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import ImagePlaceholder from '../../utils/ImagePlaceholder'
import { ImageWithFallback } from '../../utils/ImageWithFallback'
import service1 from '../../assets/service/service-1.jpg'
import service2 from '../../assets/service/service-2.jpg'
import service3 from '../../assets/service/service-3.jpg'
import service4 from '../../assets/service/service-4.jpg'
import service5 from '../../assets/service/service-5.jpg'
import service6 from '../../assets/service/service-6.jpg'
import landscapeImage from '../../assets/landscape.jpeg'

export function ServicesComponent() {
  const navigate = useNavigate()
  const services = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Lawn Care & Maintenance',
      description:
        'Complete lawn care including mowing, edging, fertilizing, and seasonal treatments.',
      features: [
        'Weekly/Bi-weekly mowing',
        'Fertilization programs',
        'Weed control',
        'Aeration & overseeding',
      ],
      image: service1,
      price: 'Starting at $75/visit',
      popular: true,
    },
    {
      icon: <Flower className="w-8 h-8" />,
      title: 'Garden Design & Installation',
      description:
        'Custom garden designs that reflect your style and complement your property.',
      features: [
        '3D design concepts',
        'Plant selection',
        'Installation',
        'Seasonal planning',
      ],
      image:
        service2,
      price: 'From $2,500',
      popular: false,
    },
    {
      icon: <TreePine className="w-8 h-8" />,
      title: 'Tree & Shrub Services',
      description:
        'Professional tree care including pruning, removal, and health assessments.',
      features: [
        'Tree pruning & trimming',
        'Disease treatment',
        'Tree removal',
        'Stump grinding',
      ],
      image:
        service3,
      price: 'Quote on request',
      popular: false,
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: 'Irrigation Systems',
      description:
        'Efficient watering solutions to keep your landscape healthy year-round.',
      features: [
        'Sprinkler installation',
        'Drip irrigation',
        'Smart controllers',
        'System maintenance',
      ],
      image:
        service4,
      price: 'From $1,200',
      popular: false,
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Hardscaping',
      description:
        'Beautiful patios, walkways, and outdoor living spaces that last.',
      features: [
        'Patio installation',
        'Walkway design',
        'Retaining walls',
        'Outdoor kitchens',
      ],
      image:
        service5,
      price: 'From $5,000',
      popular: false,
    },
    {
      icon: <Sprout className="w-8 h-8" />,
      title: 'Seasonal Cleanup',
      description:
        'Comprehensive seasonal services to prepare your landscape for every season.',
      features: [
        'Spring cleanup',
        'Fall leaf removal',
        'Winter preparation',
        'Mulching',
      ],
      image:
        service6,
      price: 'Starting at $150',
      popular: false,
    },
  ]

  const benefits = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      title: 'Licensed & Insured',
      description:
        'Fully licensed professionals with comprehensive insurance coverage',
    },
    {
      icon: <Clock className="w-6 h-6 text-green-600" />,
      title: 'Reliable Service',
      description: 'On-time service with consistent quality you can depend on',
    },
    {
      icon: <Shield className="w-6 h-6 text-green-600" />,
      title: 'Satisfaction Guarantee',
      description:
        '100% satisfaction guarantee on all our landscaping services',
    },
  ]

  return (
    <>
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="mb-4 bg-green-100 text-green-800 border-green-200">
              <Leaf className="w-4 h-4 mr-2" />
              Our Services
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Complete Landscaping Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From weekly lawn maintenance to complete landscape transformations,
              we offer comprehensive services to keep your outdoor space beautiful
              year-round.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="relative group"
                onClick={() => navigate('/services/1')}
              >
                <Card className="h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white group-hover:border-green-200">
                  {service.popular && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className="bg-green-600 text-white">
                        Most Popular
                      </Badge>
                    </div>
                  )}

                  {/* Service Image */}
                  <div className="relative h-48 overflow-hidden">
                    <ImageWithFallback
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* <ImagePlaceholder width={384} height={192} /> */}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>

                    {/* Service Icon */}
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-full flex items-center justify-center text-green-600 shadow-lg">
                      {service.icon}
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Includes:
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <li
                            key={i}
                            className="flex items-center text-sm text-gray-600"
                          >
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <span className="text-lg font-semibold text-green-600">
                          {service.price}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="group-hover:bg-green-600 group-hover:text-white group-hover:border-green-600 transition-all"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Benefits Section */}
          <motion.div
            className="grid md:grid-cols-3 gap-8 mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="mb-4 flex justify-center">{benefit.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center bg-green-600 rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ backgroundImage: `url(${landscapeImage})` }}
          >
            <h3 className="text-2xl font-bold mb-4">
              Ready to Transform Your Landscape?
            </h3>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Get a free consultation and customized quote for your landscaping
              project. Our experts will help you create the outdoor space of your
              dreams.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={'/contact'}>
                <Button
                  size="lg"
                  className="bg-white text-green-600 hover:bg-gray-100"
                >
                  Get Free Estimate
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                Call (555) 123-4567
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}


